<template>
  <div class="community-comment-item d-flex" :class="{ 'is-reply': isReply }">
    <div class="comment-avatar">
      <img v-if="authorPhoto" :src="authorPhoto" :alt="authorName" />
      <span v-else class="letter">{{ authorInitial }}</span>
    </div>

    <div class="comment-content flex-fill">
      <div class="d-flex align-items-center">
        <span class="author-name">{{ authorName }}</span>
        <small class="comment-date ml-2">
          {{ createdAt }}
          <template v-if="comment.edited_at"> &middot; editado</template>
        </small>
        <b-dropdown
          v-if="isOwner"
          class="ml-auto"
          variant="link"
          toggle-class="text-decoration-none"
          no-caret
          right
        >
          <template #button-content>
            <i class="icon-options" />
          </template>
          <b-dropdown-item @click="startEdit">
            <i class="icon-edit-filled" /> Editar
          </b-dropdown-item>
          <b-dropdown-item @click="remove">
            <i class="icon-trash" /> Excluir
          </b-dropdown-item>
        </b-dropdown>
      </div>

      <div v-if="!editing" class="comment-text mt-1">{{ comment.text }}</div>
      <form v-else class="edit-form d-flex mt-1" @submit.prevent="saveEdit">
        <input v-model="editText" class="form-control comment-input" />
        <button
          class="btn btn-primary ml-2"
          type="submit"
          :disabled="loading || !editText.trim()"
        >
          <loading :show="loading" small>Salvar</loading>
        </button>
        <button
          class="btn btn-outline-secondary ml-1"
          type="button"
          @click="editing = false"
        >
          Cancelar
        </button>
      </form>

      <div v-if="!isReply" class="comment-actions mt-1">
        <button class="btn-link-action" @click="replying = !replying">
          Responder
        </button>
      </div>

      <form
        v-if="replying"
        class="reply-form d-flex mt-2"
        @submit.prevent="sendReply"
      >
        <input
          v-model="replyText"
          class="form-control comment-input"
          placeholder="Escreva uma resposta..."
        />
        <button
          class="btn btn-primary ml-2"
          type="submit"
          :disabled="loading || !replyText.trim()"
        >
          <loading :show="loading" small>Responder</loading>
        </button>
      </form>

      <div v-if="replies.length" class="replies mt-2">
        <comment-item
          v-for="reply in replies"
          :key="reply.id"
          :comment="reply"
          :post-id="postId"
          is-reply
          @changed="$emit('changed')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { timeFromNow } from "@/functions/helpers";

export default {
  name: "CommentItem",
  props: {
    comment: {
      type: Object,
      required: true
    },
    postId: {
      type: String,
      required: true
    },
    isReply: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      editing: false,
      replying: false,
      loading: false,
      editText: this.comment.text,
      replyText: ""
    };
  },
  computed: {
    author() {
      return this.comment.author || {};
    },
    authorName() {
      return this.author.name || "Usuário";
    },
    authorPhoto() {
      return this.author.photo;
    },
    authorInitial() {
      return (this.authorName[0] || "?").toUpperCase();
    },
    isOwner() {
      return !!(
        this.$user.user &&
        this.author &&
        this.author.id === this.$user.user.id
      );
    },
    createdAt() {
      return timeFromNow(this.comment.created_at);
    },
    replies() {
      return this.comment.replies || [];
    }
  },
  methods: {
    startEdit() {
      this.editText = this.comment.text;
      this.editing = true;
    },
    saveEdit() {
      if (!this.editText.trim()) return;
      this.loading = true;
      this.$store
        .dispatch("community/editComment", {
          commentId: this.comment.id,
          text: this.editText
        })
        .then(() => {
          this.editing = false;
          this.$emit("changed");
        })
        .catch(() => this.$message.error("Não foi possível editar"))
        .finally(() => {
          this.loading = false;
        });
    },
    remove() {
      this.loading = true;
      this.$store
        .dispatch("community/deleteComment", this.comment.id)
        .then(() => this.$emit("changed"))
        .catch(() => this.$message.error("Não foi possível excluir"))
        .finally(() => {
          this.loading = false;
        });
    },
    sendReply() {
      if (!this.replyText.trim()) return;
      this.loading = true;
      this.$store
        .dispatch("community/comment", {
          postId: this.postId,
          text: this.replyText.trim(),
          parent_id: this.comment.id
        })
        .then(() => {
          this.replyText = "";
          this.replying = false;
          this.$emit("changed");
        })
        .catch(error => {
          const message =
            error?.response?.data?.non_field_errors?.[0] ||
            "Não foi possível responder";
          this.$message.error(message);
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.community-comment-item {
  gap: 10px;
  padding: 8px 0;

  &.is-reply {
    padding-left: 8px;
  }

  .comment-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(128, 128, 128, 0.2);
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .letter {
      font-size: 14px;
      font-weight: 500;
    }
  }

  .author-name {
    font-weight: 500;
    font-size: 13px;
  }

  .comment-date {
    font-size: 11px;
    opacity: 0.7;
  }

  .comment-text {
    font-size: 14px;
    overflow-wrap: anywhere;
  }

  .comment-input {
    min-height: 40px;
    border-radius: 10px;
    font-size: 14px;
  }

  .btn-link-action {
    border: none;
    background: transparent;
    padding: 0;
    color: inherit;
    font-size: 13px;
    opacity: 0.8;
  }

  .replies {
    border-left: 2px solid rgba(128, 128, 128, 0.2);
    padding-left: 10px;
  }
}
</style>
